<script setup lang="ts">
import { computed, type PropType, ref } from 'vue';
import { IconPosition, type CustomIcon } from './interfaces/CustomIcon';
import { ButtonType } from '@/components';
import ProgressSpinner from 'primevue/progressspinner';

const props = defineProps({
  icon: {
    type: Object as PropType<CustomIcon>
  },
  text: {
    type: String
  },
  fontWeight: {
    type: Number
  },
  type: {
    type: String as PropType<ButtonType>
  },
  marginLeft: {
    type: Number,
    default: 0
  },
  disabled: {
    type: Boolean
  },
  loading: {
    type: Boolean
  },
  tooltip: {
    type: String
  }
});

const emit = defineEmits(['onClick']);

const handleClick = (event: MouseEvent) => {
  emit('onClick', event);
};

const marginStart = computed(() => `${props.marginLeft}px`);
const iconMarginRight = computed(() => props.text ? `12px` : `0px`);

const buttonClass = computed(() => `${props.type ? props.type + ' ' : ''}${props.loading ? 'loading ' : ''}`.trim());

const buttonRef = ref(null);

defineExpose({
  buttonRef
});
</script>

<template>
  <button v-ripple
    @click="handleClick"
    ref="buttonRef"
    :disabled="disabled || loading"
    :class="buttonClass"
    :style="{
      '--margin-start': marginStart,
      '--icon-margin-right': icon?.position != IconPosition.END ? iconMarginRight : undefined,
      '--icon-margin-left': icon?.position == IconPosition.END ? iconMarginRight : undefined,
      '--font-weight': fontWeight}"
    v-tooltip="tooltip">
      <FontAwesomeIcon :icon="[`${icon.style}`, `${icon.icon}`]"
        class="icon"
        v-if="icon && !loading && icon.position != IconPosition.END"/>
      <ProgressSpinner v-if="loading" strokeWidth="4"/>
      <div class="text" v-if="text">{{ text }}</div>
      <FontAwesomeIcon :icon="[`${icon.style}`, `${icon.icon}`]"
        class="icon"
        v-if="icon && !loading && icon.position == IconPosition.END"/>
  </button>
</template>

<style scoped lang="scss">
:deep(.p-progressspinner) {
  margin: unset;
}
@import '../../assets/_custom.button.scss';
</style>
